<template>
  <div class="fa-layout">
    <header v-if="header" class="fa-layout__header" :style="style.header">
      <slot name="header" />
    </header>

    <main class="fa-layout__main" :style="style.main">
      <aside
        v-if="left"
        class="fa-layout__left"
        :class="{
          'is-collapsed': leftHandle.hidden
        }"
        :style="style.left"
      >
        <slot name="left" />

        <a
          v-if="leftCollapsible"
          class="fa-layout__toggle fa-layout__toggle-left fa-icon-unfold"
          :title="leftHandle.tips"
          @click="leftHandle.toggle"
        ></a>
      </aside>

      <aside
        v-if="right"
        class="fa-layout__right"
        :class="{
          'is-collapsed': rightHandle.hidden
        }"
        :style="style.right"
      >
        <slot name="right" />

        <a
          v-if="rightCollapsible"
          class="fa-layout__toggle fa-layout__toggle-right fa-icon-unfold"
          :title="rightHandle.tips"
          @click="rightHandle.toggle"
        ></a>
      </aside>

      <div class="fa-layout__body"><slot /></div>
    </main>

    <footer v-if="footer" class="fa-layout__footer" :style="style.footer">
      <slot name="footer" />
    </footer>

    <slot name="main" />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, reactive, ref, watch } from 'vue'

/** 布局默认尺寸 */
const DefaultSize = {
  header: 48,
  left: 200,
  right: 200,
  footer: 48
}

/** 尺寸值类型 */
type SizeValue = boolean | number | string

/** 获取尺寸 */
const getSize = (type: keyof typeof DefaultSize, size: SizeValue) =>
  ((size === true && `${DefaultSize[type]}px`) ||
    (!size && 0) ||
    (typeof size === 'number' && `${size}px`) ||
    size) as string

/** 展开收起 */
function useToggle(onToggle?: (visible: boolean) => void) {
  /** 是否收起 */
  const hidden = ref(false)

  watch(hidden, () => onToggle?.(!hidden.value))

  return reactive({
    hidden,

    /** 提示 */
    tips: computed(() => (hidden.value ? '展开' : '收起')),

    /** 切换收起展开 */
    toggle() {
      hidden.value = !hidden.value
    }
  })
}

/** 页面布局 */
export default defineComponent({
  name: 'FaLayout',

  props: {
    /** 头部 */
    header: {
      type: [Boolean, Number, String],
      default: true
    },

    /** 左边栏 */
    left: {
      type: [Boolean, Number, String],
      default: true
    },

    /** 左边栏是否可收起 */
    leftCollapsible: {
      type: Boolean,
      default: true
    },

    /** 右边栏 */
    right: {
      type: [Boolean, Number, String],
      default: true
    },

    /** 右边栏是否可收起 */
    rightCollapsible: {
      type: Boolean,
      default: true
    },

    /** 是否显示底栏 */
    footer: {
      type: [Boolean, Number, String],
      default: false
    }
  },

  emits: [
    /** 展开收起 `toggle(side: 'left' | 'right',visible: boolean)` */
    'toggle'
  ],

  setup(props, { emit }) {
    /** 样式 */
    const style = computed(() => {
      const headerHeight = getSize('header', props.header)
      const leftWidth = getSize('left', props.left)
      const rightWidth = getSize('right', props.right)
      const footerHeight = getSize('footer', props.footer)

      return {
        /** 头部 */
        header: {
          height: headerHeight
        },

        /** 主体 */
        main: {
          top: props.header ? headerHeight : 0,
          bottom: props.footer ? footerHeight : 0
        },

        /** 左栏 */
        left: {
          width: leftWidth
        },

        /** 右栏 */
        right: {
          width: rightWidth
        },

        /** 底栏 */
        footer: {
          height: footerHeight
        }
      }
    })

    return {
      style,

      /** 左栏句柄 */
      leftHandle: useToggle(visible => emit('toggle', 'left', visible)),

      /** 右栏句柄 */
      rightHandle: useToggle(visible => emit('toggle', 'right', visible))
    }
  }
})
</script>

<style lang="scss">
/* 布局 */
// 头部高度
$layout-hd-height: 48px;
// 左栏宽度
$layout-left-width: 200px;
// 右栏宽度
$layout-right-width: 200px;
// 底部高度
$layout-ft-height: 48px;

.fa-layout {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  &__header {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: $layout-hd-height;
    transition: height 0.3s ease-in-out;
  }

  &__header,
  &__left,
  &__right,
  &__body,
  &__footer {
    box-sizing: border-box;
  }

  &__main {
    position: absolute;
    top: $layout-hd-height;
    right: 0;
    bottom: $layout-ft-height;
    left: 0;
    display: block;
  }

  &__left,
  &__right,
  &__body {
    position: relative;
    height: 100%;
  }

  $toggle-radius: 6px;

  &__toggle {
    $w: 10px;
    $h: 28px;

    @extend .fa-btn-light;

    position: absolute;
    top: 50%;
    z-index: 1;
    width: $w;
    height: $h;
    margin-top: math.div(-$h, 2);
    line-height: $h;

    &::before {
      display: inline-block;
      font-size: 14px;
      line-height: $h;
    }
  }

  &__left,
  &__right {
    transition: width 0.3s ease-in-out;

    &.is-collapsed {
      width: 0 !important;
    }
  }

  &__left {
    float: left;
    width: $layout-left-width;

    .fa-layout__toggle {
      left: 100%;
      text-indent: -2px;
      border-radius: 0px $toggle-radius $toggle-radius 0px;
      box-shadow: 2px 1px 4px 0px rgba(0, 0, 0, 0.05);

      &::before {
        transform: rotate(180deg);
      }
    }

    &.is-collapsed .fa-layout__toggle {
      text-indent: -1px;

      &::before {
        transform: rotate(0);
      }
    }
  }

  &__right {
    float: right;
    width: $layout-right-width;
    transition: width 0.3s ease-in-out;

    .fa-layout__toggle {
      right: 100%;
      text-indent: -1px;
      border-radius: $toggle-radius 0px 0px $toggle-radius;
      box-shadow: -2px 1px 4px 0px rgba(0, 0, 0, 0.05);
    }

    &.is-collapsed .fa-layout__toggle {
      text-indent: -2px;

      &::before {
        transform: rotate(180deg);
      }
    }
  }

  &__body {
    overflow: auto;
  }

  &__footer {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: $layout-ft-height;
  }
}
</style>
